import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { MerchandiserEntity } from './merchandiser.entity';
import { SensorStatus } from './sensor.entity';

/**
 * A physical IoT "sensor device" — the array unit that sits on top of a
 * merchandiser (ice chest, and later other placed units) and is wired to the
 * depth sensors that read fill level.
 *
 * PD-13928. The device's transmissions carry two things at once: a PARENT
 * record about the unit itself (GPS, cellular/battery/power telemetry, firmware)
 * plus a list of CHILD readings, one per depth sensor. Previously only the child
 * sensors were modeled (grouped implicitly by the shared `device_id` string) and
 * the parent data — including GPS — was dropped on the floor. This makes the
 * unit a real entity that:
 *   - owns its child `sensor` rows (`sensor.fk_sensor_device__id`),
 *   - links to the merchandiser it's mounted on (`fk_merchandiser__id`, NULL
 *     until someone attaches it — devices exist by communicating, before being
 *     married to a merchandiser),
 *   - and stores the unit's latest GPS + health/telemetry, parsed from each
 *     transmission.
 *
 * Device-level fields that used to live (misleadingly) on every `sensor` module
 * row — status, powerOn, lastSeen, lastErrorMessage, firmwareVersion,
 * hardwareModel — now live here, since the whole array shares them.
 */
@Index('sensor_device_device_id_uindex', ['deviceId'], { unique: true })
@Index('sensor_device_merchandiser_idx', ['merchandiserId'])
// auditable: false — the device's telemetry (status/lastSeen/GPS) is refreshed
// on every transmission (~10s cadence); auditing each refresh would flood
// `_audit`. Mirrors sensor_submission. Meaningful changes (attach to a
// merchandiser) are infrequent; explicit logging can be added later if needed.
@Entity('sensor_device', {}, { auditable: false })
export class SensorDeviceEntity extends BaseEntity {
	/** The unit's unique serial number (payload `device.sn`). */
	@Column('varchar', { name: 'device_id', length: 128 })
	public deviceId!: string;

	@Column('varchar', { name: 'name', nullable: true, length: 255 })
	public name!: string | null;

	/** Payload `device.mfn` — e.g. "MotorCityICE". */
	@Column('varchar', { name: 'manufacturer', nullable: true, length: 128 })
	public manufacturer!: string | null;

	/** Payload `device.dn` — the unit kind, e.g. "refrigerator". */
	@Column('varchar', { name: 'device_name', nullable: true, length: 128 })
	public deviceName!: string | null;

	/**
	 * The merchandiser this unit is mounted on. NULL = Unattached (the unit has
	 * communicated but hasn't been married to a merchandiser yet).
	 */
	@Column('int', {
		name: 'fk_merchandiser__id',
		unsigned: true,
		nullable: true,
	})
	public merchandiserId!: number | null;

	// --- Device-level health (moved off the per-module sensor row) ---

	@Column('enum', {
		name: 'status',
		enum: SensorStatus,
		default: SensorStatus.OFFLINE,
	})
	public status!: SensorStatus;

	@Column('boolean', { name: 'power_on', default: false })
	public powerOn!: boolean;

	@Column('datetime', { name: 'last_seen', nullable: true })
	public lastSeen!: Date | null;

	@Column('text', { name: 'last_error_message', nullable: true })
	public lastErrorMessage!: string | null;

	@Column('varchar', { name: 'firmware_version', nullable: true, length: 64 })
	public firmwareVersion!: string | null;

	@Column('varchar', { name: 'hardware_model', nullable: true, length: 128 })
	public hardwareModel!: string | null;

	// --- Last-known parent telemetry (from the payload `status` block) ---

	/** Battery voltage in mV (payload status.vbat). */
	@Column('int', { name: 'battery_voltage', nullable: true })
	public batteryVoltage!: number | null;

	/** Device (4G module) temperature in °C (payload status.tmpr). */
	@Column('int', { name: 'device_temperature', nullable: true })
	public deviceTemperature!: number | null;

	/** Cellular signal strength in dBm (payload status.ss). */
	@Column('int', { name: 'cellular_signal_strength', nullable: true })
	public cellularSignalStrength!: number | null;

	/** Cellular signal quality 0-31 (payload status.csq). */
	@Column('int', { name: 'cellular_signal_quality', nullable: true })
	public cellularSignalQuality!: number | null;

	/** Power source: 1 = AC, 0 = battery (payload status.pwr). */
	@Column('int', { name: 'power_source', nullable: true })
	public powerSource!: number | null;

	/** Whether the unit is currently charging (payload status.chg: 1 = charging). */
	@Column('boolean', { name: 'charging', nullable: true })
	public charging!: boolean | null;

	/**
	 * Last GPS fix quality (payload location.gnss.gpss: 4=RTK, 3=Float, 2=Diff,
	 * 1=Single, 0=Invalid) — lets the map de-emphasize a weak fix.
	 */
	@Column('int', { name: 'gps_fix_quality', nullable: true })
	public gpsFixQuality!: number | null;

	/** Satellites used in the last fix (payload location.gnss.sat). */
	@Column('int', { name: 'satellite_count', nullable: true })
	public satelliteCount!: number | null;

	// --- Last-known GPS (payload location.gnss; only stored on a valid fix) ---

	@Column('decimal', {
		name: 'current_latitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLatitude!: string | null;

	@Column('decimal', {
		name: 'current_longitude',
		precision: 10,
		scale: 7,
		nullable: true,
	})
	public currentLongitude!: string | null;

	@Column('datetime', { name: 'current_location_at', nullable: true })
	public currentLocationAt!: Date | null;

	@ManyToOne(() => MerchandiserEntity)
	@JoinColumn({ name: 'fk_merchandiser__id' })
	public fkMerchandiser!: MerchandiserEntity | null;
}
